import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownLeft, ArrowUpRight, PackagePlus } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useAddProductUnitMutation, useAddProductVariantMutation, useBrandsQuery,
  useCategoriesQuery, useDeleteProductUnitMutation, useDeleteProductVariantMutation,
  useProductQuery, useStockLedgerQuery, useUnitsQuery, useUpdateProductMutation,
  useUpdateProductVariantMutation,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDateTime, formatMoney, formatQuantity } from '@/lib/format';
import {
  Alert, Badge, Button, Checkbox, ErrorState, Input, LoadingState, Modal, Select, Spinner,
  Table, Td, Textarea, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { UnitPicker } from '@/components/UnitPicker';
import { CategoryPicker } from '@/components/CategoryPicker';
import { BrandPicker } from '@/components/BrandPicker';

/** What the editor puts in a field when the product carries no value for it. */
const FIELD_DEFAULTS: Record<string, string> = {
  trackInventory: 'true',
  trackBatches: 'false',
  trackExpiry: 'false',
  trackSerials: 'false',
  isSellable: 'true',
  isPurchasable: 'true',
  type: 'STOCKED',
};

const defaultsFor = (key: string): string => FIELD_DEFAULTS[key] ?? '';

/**
 * Variants and alternative units for one product.
 *
 * These are the two things that cannot be set at creation time: a variant needs
 * its own SKU, and an alternative unit needs a conversion factor against the
 * base unit the product was just given.
 */
export function ProductDetailModal({
  productId,
  onClose,
}: {
  productId: string;
  onClose: () => void;
}) {
  const format = useFormat();
  const { data: product, isLoading, isError, error, refetch } = useProductQuery(productId);
  const { data: units } = useUnitsQuery({ limit: 100, isActive: true });
  const movements = useStockLedgerQuery({ productId, limit: 12 });

  // Stock is held against variants, so restocking needs one. The default is the
  // right guess for a simple product and the honest starting point for the rest.
  const defaultVariantId =
    product?.variants?.find((variant) => variant.isDefault)?.id ?? product?.variants?.[0]?.id;

  const { data: categories } = useCategoriesQuery({ limit: 200 });
  const { data: brands } = useBrandsQuery({ limit: 200 });

  const [updateProduct, updateState] = useUpdateProductMutation();
  const [addVariant, addVariantState] = useAddProductVariantMutation();
  const [removeVariant] = useDeleteProductVariantMutation();
  const [addUnit, addUnitState] = useAddProductUnitMutation();
  const [removeUnit] = useDeleteProductUnitMutation();

  /** Editable copy of the product's own fields, synced when it loads. */
  const [details, setDetails] = useState<Record<string, string> | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!product) return;
    setDetails({
      name: product.name,
      sku: product.sku,
      barcode: product.barcode ?? '',
      description: product.description ?? '',
      type: product.type ?? 'STOCKED',
      status: product.status,
      categoryId: product.categoryId ?? '',
      brandId: product.brandId ?? '',
      costPrice: product.costPrice,
      sellingPrice: product.sellingPrice,
      wholesalePrice: product.wholesalePrice ?? '',
      reorderLevel: product.reorderLevel ?? '',
      minStock: product.minStock ?? '',
      maxStock: product.maxStock ?? '',
      trackInventory: String(product.trackInventory ?? true),
      trackBatches: String(product.trackBatches ?? false),
      trackExpiry: String(product.trackExpiry ?? false),
      trackSerials: String(product.trackSerials ?? false),
      isSellable: String(product.isSellable ?? true),
      isPurchasable: String(product.isPurchasable ?? true),
    });
  }, [product]);

  const [variantForm, setVariantForm] = useState({ name: '', sku: '', sellingPrice: '' });
  // Editing an existing variant, keyed by its id so only one row is in edit.
  const [editingVariant, setEditingVariant] = useState<string | null>(null);
  const [variantEdit, setVariantEdit] = useState({ name: '', sku: '', sellingPrice: '', costPrice: '' });
  const [updateVariant, updateVariantState] = useUpdateProductVariantMutation();
  const [unitForm, setUnitForm] = useState({ unitId: '', factor: '' });
  const [actionError, setActionError] = useState<string | null>(null);

  /**
   * Compares the form against the product field by field, so Save stays off
   * until something has genuinely changed. Built from the loaded product rather
   * than a hand-written list, which is what let fields drift out of it before.
   */
  const dirty =
    product !== undefined &&
    details !== null &&
    Object.entries(details).some(([key, value]) => {
      // The form holds strings, and the loader above filled every one of them
      // from the product. Comparing against the same defaults keeps a field the
      // API simply did not send from reading as an edit.
      const original = (product as unknown as Record<string, unknown>)[key];
      if (original === undefined || original === null) return value !== defaultsFor(key);
      if (typeof original === 'boolean') return value !== String(original);
      return value !== String(original);
    });

  const saveDetails = async (): Promise<void> => {
    if (!details) return;
    setActionError(null);
    setSaved(false);
    try {
      await updateProduct({
        id: productId,
        body: {
          name: details.name,
          sku: details.sku,
          barcode: details.barcode || undefined,
          description: details.description || undefined,
          type: details.type,
          status: details.status,
          // An empty picker means "no category", which the API takes as null.
          categoryId: details.categoryId || null,
          brandId: details.brandId || null,
          costPrice: Number(details.costPrice),
          sellingPrice: Number(details.sellingPrice),
          ...(details.wholesalePrice === ''
            ? {}
            : { wholesalePrice: Number(details.wholesalePrice) }),
          ...(details.reorderLevel === '' ? {} : { reorderLevel: Number(details.reorderLevel) }),
          ...(details.minStock === '' ? {} : { minStock: Number(details.minStock) }),
          ...(details.maxStock === '' ? {} : { maxStock: Number(details.maxStock) }),
          trackInventory: details.trackInventory === 'true',
          trackBatches: details.trackBatches === 'true',
          trackExpiry: details.trackExpiry === 'true',
          trackSerials: details.trackSerials === 'true',
          isSellable: details.isSellable === 'true',
          isPurchasable: details.isPurchasable === 'true',
        },
      }).unwrap();
      setSaved(true);
    } catch (failure) {
      setActionError(apiErrorMessage(failure, 'Those changes could not be saved'));
    }
  };

  const saveVariant = async (variantId: string): Promise<void> => {
    setActionError(null);
    try {
      await updateVariant({
        productId,
        variantId,
        body: {
          name: variantEdit.name,
          sku: variantEdit.sku,
          ...(variantEdit.sellingPrice === ''
            ? {}
            : { sellingPrice: Number(variantEdit.sellingPrice) }),
          ...(variantEdit.costPrice === '' ? {} : { costPrice: Number(variantEdit.costPrice) }),
        },
      }).unwrap();
      setEditingVariant(null);
    } catch (failure) {
      setActionError(apiErrorMessage(failure, 'That variant could not be saved'));
    }
  };

  const submitVariant = async (): Promise<void> => {
    setActionError(null);
    try {
      await addVariant({
        productId,
        body: {
          name: variantForm.name,
          sku: variantForm.sku,
          sellingPrice: variantForm.sellingPrice ? Number(variantForm.sellingPrice) : undefined,
        },
      }).unwrap();
      setVariantForm({ name: '', sku: '', sellingPrice: '' });
    } catch (failure) {
      setActionError(apiErrorMessage(failure, 'That variant could not be added'));
    }
  };

  const submitUnit = async (): Promise<void> => {
    setActionError(null);
    try {
      await addUnit({
        productId,
        body: { unitId: unitForm.unitId, factor: Number(unitForm.factor) },
      }).unwrap();
      setUnitForm({ unitId: '', factor: '' });
    } catch (failure) {
      setActionError(apiErrorMessage(failure, 'That unit could not be added'));
    }
  };

  const drop = async (run: () => Promise<unknown>, fallback: string): Promise<void> => {
    setActionError(null);
    try {
      await run();
    } catch (failure) {
      setActionError(apiErrorMessage(failure, fallback));
    }
  };

  // Units already configured, plus the base unit, cannot be added again.
  const takenUnitIds = new Set([
    product?.baseUnitId,
    ...(product?.productUnits ?? []).map((row) => row.unitId),
  ]);

  return (
    <Modal
      open
      onClose={onClose}
      title={product ? product.name : 'Product'}
      description={product ? `${product.sku} · base unit ${product.baseUnit?.symbol ?? ''}` : undefined}
      size="lg"
      footer={<Button variant="outline" onClick={onClose}>Close</Button>}
    >
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : product ? (
        <div className="space-y-6">
          {actionError && <Alert tone="danger">{actionError}</Alert>}

          {details && (
            <section>
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-medium">Details</h3>
                <Can permission={P.PRODUCT_UPDATE}>
                  <Button
                    size="sm"
                    variant="outline"
                    loading={updateState.isLoading}
                    disabled={!dirty || !details.name || !details.sku}
                    onClick={() => void saveDetails()}
                  >
                    Save changes
                  </Button>
                </Can>
              </div>

              {saved && !dirty && <Alert tone="success">Saved.</Alert>}

              <Can
                permission={P.PRODUCT_UPDATE}
                fallback={
                  <p className="text-xs text-muted">
                    You do not have permission to change this product.
                  </p>
                }
              >
                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input
                      label="Name"
                      value={details.name}
                      onChange={(event) => setDetails({ ...details, name: event.target.value })}
                    />
                    <Input
                      label="SKU"
                      value={details.sku}
                      onChange={(event) =>
                        setDetails({ ...details, sku: event.target.value.toUpperCase() })
                      }
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input
                      label="Barcode"
                      value={details.barcode}
                      onChange={(event) => setDetails({ ...details, barcode: event.target.value })}
                    />
                    <Select
                      label="Status"
                      value={details.status}
                      onChange={(event) => setDetails({ ...details, status: event.target.value })}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="DISCONTINUED">Discontinued</option>
                    </Select>
                    <Select
                      label="Kind"
                      value={details.type}
                      onChange={(event) => setDetails({ ...details, type: event.target.value })}
                      hint="A service or non-stocked item keeps no ledger."
                    >
                      <option value="STOCKED">Stocked</option>
                      <option value="SERVICE">Service</option>
                      <option value="NON_STOCKED">Non-stocked</option>
                      <option value="BUNDLE">Bundle</option>
                      <option value="RAW_MATERIAL">Raw material</option>
                      <option value="FINISHED_GOOD">Finished good</option>
                    </Select>
                  </div>

                  <Textarea
                    label="Description"
                    rows={2}
                    value={details.description}
                    onChange={(event) =>
                      setDetails({ ...details, description: event.target.value })
                    }
                  />

                  <div className="grid gap-3 sm:grid-cols-2">
                    <CategoryPicker
                      value={details.categoryId}
                      onChange={(categoryId) => setDetails({ ...details, categoryId })}
                      categories={categories?.items ?? []}
                    />
                    <BrandPicker
                      value={details.brandId}
                      onChange={(brandId) => setDetails({ ...details, brandId })}
                      brands={brands?.items ?? []}
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <Input
                      label="Cost price"
                      type="number"
                      min="0"
                      step="0.0001"
                      value={details.costPrice}
                      onChange={(event) => setDetails({ ...details, costPrice: event.target.value })}
                    />
                    <Input
                      label="Selling price"
                      type="number"
                      min="0"
                      step="0.0001"
                      value={details.sellingPrice}
                      onChange={(event) =>
                        setDetails({ ...details, sellingPrice: event.target.value })
                      }
                    />
                    <Input
                      label="Reorder level"
                      type="number"
                      min="0"
                      step="0.0001"
                      value={details.reorderLevel}
                      onChange={(event) =>
                        setDetails({ ...details, reorderLevel: event.target.value })
                      }
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <Input
                      label="Wholesale price"
                      type="number"
                      min="0"
                      step="0.0001"
                      value={details.wholesalePrice}
                      onChange={(event) =>
                        setDetails({ ...details, wholesalePrice: event.target.value })
                      }
                    />
                    <Input
                      label="Minimum stock"
                      type="number"
                      min="0"
                      step="0.0001"
                      value={details.minStock}
                      onChange={(event) => setDetails({ ...details, minStock: event.target.value })}
                    />
                    <Input
                      label="Maximum stock"
                      type="number"
                      min="0"
                      step="0.0001"
                      value={details.maxStock}
                      onChange={(event) => setDetails({ ...details, maxStock: event.target.value })}
                    />
                  </div>

                  <fieldset className="rounded-lg border border-subtle p-3">
                    <legend className="px-1 text-sm font-medium">How it is handled</legend>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <Checkbox
                        label="Tracks inventory"
                        checked={details.trackInventory === 'true'}
                        onChange={(event) =>
                          setDetails({ ...details, trackInventory: String(event.target.checked) })
                        }
                      />
                      <Checkbox
                        label="Tracks batches"
                        checked={details.trackBatches === 'true'}
                        onChange={(event) =>
                          setDetails({ ...details, trackBatches: String(event.target.checked) })
                        }
                      />
                      <Checkbox
                        label="Tracks expiry"
                        checked={details.trackExpiry === 'true'}
                        onChange={(event) =>
                          setDetails({ ...details, trackExpiry: String(event.target.checked) })
                        }
                      />
                      <Checkbox
                        label="Tracks serial numbers"
                        checked={details.trackSerials === 'true'}
                        onChange={(event) =>
                          setDetails({ ...details, trackSerials: String(event.target.checked) })
                        }
                      />
                      <Checkbox
                        label="Can be sold"
                        checked={details.isSellable === 'true'}
                        onChange={(event) =>
                          setDetails({ ...details, isSellable: String(event.target.checked) })
                        }
                      />
                      <Checkbox
                        label="Can be bought"
                        checked={details.isPurchasable === 'true'}
                        onChange={(event) =>
                          setDetails({ ...details, isPurchasable: String(event.target.checked) })
                        }
                      />
                    </div>
                  </fieldset>

                  <p className="text-xs text-muted">
                    The base unit is {product.baseUnit?.name ?? 'set'} and cannot change once the
                    product has stock movements.
                  </p>
                </div>
              </Can>
            </section>
          )}

          <section>
            <h3 className="mb-2 text-sm font-medium">Variants</h3>
            <p className="mb-3 text-xs text-muted">
              Stock is held against variants, never the product itself, so there is always at
              least one.
            </p>

            <Table>
              <thead>
                <tr>
                  <Th>Variant</Th>
                  <Th>SKU</Th>
                  <Th numeric>Price</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {product.variants?.map((variant) => (
                  <tr key={variant.id}>
                    <Td>
                      {editingVariant === variant.id ? (
                        <Input
                          aria-label={`Name for ${variant.sku}`}
                          value={variantEdit.name}
                          onChange={(event) =>
                            setVariantEdit({ ...variantEdit, name: event.target.value })
                          }
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <span>{variant.name}</span>
                          {variant.isDefault && <Badge tone="brand">Default</Badge>}
                        </div>
                      )}
                    </Td>
                    <Td className="text-secondary">
                      {editingVariant === variant.id ? (
                        <Input
                          aria-label={`SKU for ${variant.sku}`}
                          value={variantEdit.sku}
                          onChange={(event) =>
                            setVariantEdit({ ...variantEdit, sku: event.target.value })
                          }
                        />
                      ) : (
                        variant.sku
                      )}
                    </Td>
                    <Td numeric className="text-secondary">
                      {editingVariant === variant.id ? (
                        <Input
                          aria-label={`Price for ${variant.sku}`}
                          type="number"
                          min="0"
                          step="0.0001"
                          value={variantEdit.sellingPrice}
                          onChange={(event) =>
                            setVariantEdit({ ...variantEdit, sellingPrice: event.target.value })
                          }
                        />
                      ) : variant.sellingPrice ? (
                        formatMoney(variant.sellingPrice, format)
                      ) : (
                        formatMoney(product.sellingPrice, format)
                      )}
                    </Td>
                    <Td className="text-right">
                      <Can permission={P.PRODUCT_UPDATE}>
                        {editingVariant === variant.id ? (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingVariant(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              loading={updateVariantState.isLoading}
                              disabled={!variantEdit.name || !variantEdit.sku}
                              onClick={() => void saveVariant(variant.id)}
                            >
                              Save variant
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingVariant(variant.id);
                              setVariantEdit({
                                name: variant.name,
                                sku: variant.sku,
                                sellingPrice: variant.sellingPrice ?? '',
                                costPrice: variant.costPrice ?? '',
                              });
                            }}
                          >
                            Edit
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={(product.variants?.length ?? 0) <= 1}
                          title={
                            (product.variants?.length ?? 0) <= 1
                              ? 'A product must keep at least one variant'
                              : undefined
                          }
                          onClick={() =>
                            void drop(
                              () => removeVariant({ productId, variantId: variant.id }).unwrap(),
                              'That variant could not be archived',
                            )
                          }
                        >
                          Archive
                        </Button>
                      </Can>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <Can permission={P.PRODUCT_UPDATE}>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <div className="min-w-40 flex-1">
                  <Input
                    label="Variant name"
                    value={variantForm.name}
                    onChange={(event) => setVariantForm({ ...variantForm, name: event.target.value })}
                  />
                </div>
                <div className="min-w-36 flex-1">
                  <Input
                    label="Variant SKU"
                    value={variantForm.sku}
                    onChange={(event) =>
                      setVariantForm({ ...variantForm, sku: event.target.value.toUpperCase() })
                    }
                  />
                </div>
                <div className="w-32">
                  <Input
                    label="Price"
                    type="number"
                    min="0"
                    step="0.0001"
                    value={variantForm.sellingPrice}
                    onChange={(event) =>
                      setVariantForm({ ...variantForm, sellingPrice: event.target.value })
                    }
                  />
                </div>
                <Button
                  variant="outline"
                  loading={addVariantState.isLoading}
                  disabled={!variantForm.name || !variantForm.sku}
                  onClick={() => void submitVariant()}
                >
                  Add variant
                </Button>
              </div>
            </Can>
          </section>

          <section>
            <h3 className="mb-2 text-sm font-medium">Alternative units</h3>
            <p className="mb-3 text-xs text-muted">
              Buy or sell in cartons while stock stays in {product.baseUnit?.symbol ?? 'base units'}.
              The factor says how many base units one of these holds.
            </p>

            <Table>
              <thead>
                <tr>
                  <Th>Unit</Th>
                  <Th numeric>Factor</Th>
                  <Th>Default for</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {product.productUnits?.length ? (
                  product.productUnits.map((row) => (
                    <tr key={row.id}>
                      <Td>{row.unit.name} ({row.unit.symbol})</Td>
                      <Td numeric className="text-secondary">
                        1 = {row.factor} {product.baseUnit?.symbol}
                      </Td>
                      <Td className="text-secondary">
                        {[row.isPurchaseDefault && 'purchases', row.isSalesDefault && 'sales']
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </Td>
                      <Td className="text-right">
                        <Can permission={P.UNIT_MANAGE}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              void drop(
                                () => removeUnit({ productId, productUnitId: row.id }).unwrap(),
                                'That unit could not be removed',
                              )
                            }
                          >
                            Remove
                          </Button>
                        </Can>
                      </Td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <Td className="text-muted">No alternative units.</Td>
                    <Td /><Td /><Td />
                  </tr>
                )}
              </tbody>
            </Table>

            <Can permission={P.UNIT_MANAGE}>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <div className="min-w-44 flex-1">
                  <UnitPicker
                    label="Unit"
                    value={unitForm.unitId}
                    onChange={(unitId) => setUnitForm({ ...unitForm, unitId })}
                    units={units?.items ?? []}
                    excludeIds={[...takenUnitIds].filter((id): id is string => Boolean(id))}
                  />
                </div>
                <div className="w-40">
                  <Input
                    label="Base units per"
                    type="number"
                    min="0"
                    step="0.000001"
                    value={unitForm.factor}
                    onChange={(event) => setUnitForm({ ...unitForm, factor: event.target.value })}
                  />
                </div>
                <Button
                  variant="outline"
                  loading={addUnitState.isLoading}
                  disabled={!unitForm.unitId || !unitForm.factor || Number(unitForm.factor) <= 0}
                  onClick={() => void submitUnit()}
                >
                  Add unit
                </Button>
              </div>
            </Can>
          </section>

          <Can permission={P.STOCK_VIEW}>
            <section>
              <div className="mb-2 flex items-center justify-between gap-3">
                <h3 className="text-sm font-medium">Stock history</h3>
                <Link
                  to={`/app/inventory/adjustments?reason=RESTOCK&variantId=${defaultVariantId ?? ''}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-subtle px-2.5 py-1.5 text-xs font-medium text-secondary transition-colors hover:bg-sunken"
                >
                  <PackagePlus className="size-3.5" aria-hidden />
                  Restock
                </Link>
              </div>
              <p className="mb-3 text-xs text-muted">
                Every movement this product has ever made, newest first — restocks, sales,
                receipts, transfers and write-offs alike. The ledger is the record; nothing
                here can be edited.
              </p>

              {movements.isLoading ? (
                <div className="flex justify-center py-6"><Spinner /></div>
              ) : movements.isError ? (
                <ErrorState
                  message={apiErrorMessage(movements.error)}
                  onRetry={() => void movements.refetch()}
                />
              ) : movements.data?.items.length ? (
                <Table>
                  <thead>
                    <tr>
                      <Th>When</Th>
                      <Th>Movement</Th>
                      <Th>Warehouse</Th>
                      <Th numeric>Change</Th>
                      <Th numeric>Balance</Th>
                      <Can permission={P.PRODUCT_COST_VIEW}>
                        <Th numeric>Unit cost</Th>
                      </Can>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.data.items.map((entry) => (
                      <tr key={entry.id}>
                        <Td className="whitespace-nowrap text-xs text-muted">
                          {formatDateTime(entry.occurredAt, format)}
                        </Td>
                        <Td>
                          <span className="text-sm">
                            {entry.type.toLowerCase().replaceAll('_', ' ')}
                          </span>
                          {(entry.referenceNumber || entry.notes) && (
                            <p className="text-xs text-muted">
                              {entry.referenceNumber ?? entry.notes}
                            </p>
                          )}
                        </Td>
                        <Td className="text-secondary">{entry.warehouse.name}</Td>
                        <Td numeric>
                          <span
                            className={
                              entry.direction === 'IN'
                                ? 'inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400'
                                : 'inline-flex items-center gap-1 font-medium text-red-600 dark:text-red-400'
                            }
                          >
                            {entry.direction === 'IN' ? (
                              <ArrowDownLeft className="size-3.5" aria-hidden />
                            ) : (
                              <ArrowUpRight className="size-3.5" aria-hidden />
                            )}
                            {formatQuantity(entry.quantity, format)}
                          </span>
                        </Td>
                        <Td numeric className="font-medium">
                          {formatQuantity(entry.balanceAfter, format)}
                        </Td>
                        <Can permission={P.PRODUCT_COST_VIEW}>
                          <Td numeric className="text-secondary">
                            {formatMoney(entry.unitCost, format)}
                          </Td>
                        </Can>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="rounded-lg border border-subtle p-4 text-sm text-muted">
                  This product has never moved. A restock or an opening balance will be the
                  first entry.
                </p>
              )}
            </section>
          </Can>
        </div>
      ) : null}
    </Modal>
  );
}
